/**
 * @fileoverview Public landing page shown to unauthenticated users.
 * Provides branding, feature highlights, and login/signup entry points.
 */

import { useAuth0 } from '@auth0/auth0-react';
import { useState } from 'react';
import styles from './LandingPage.module.css';

/**
 * LandingPage Component
 *
 * Public home page with branding and login/signup buttons.
 * Used for Google Cloud verification and general public-facing landing.
 *
 * @param {Object} props
 * @param {Function} props.onNavigateToPrivacy - Navigate to privacy policy
 * @param {Function} props.onNavigateToTerms - Navigate to terms of service
 */
function LandingPage({ onNavigateToPrivacy, onNavigateToTerms }) {
    const { loginWithRedirect } = useAuth0();
    const [isLoading, setIsLoading] = useState(false);

    const handleLogin = async () => {
        setIsLoading(true);
        try {
            await loginWithRedirect();
        } catch (error) {
            console.error('Login error:', error);
            setIsLoading(false);
        }
    };

    const handleSignup = async () => {
        setIsLoading(true);
        try {
            await loginWithRedirect({
                authorizationParams: {
                    screen_hint: 'signup'
                }
            });
        } catch (error) {
            console.error('Signup error:', error);
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            {/* Hero Section */}
            <header className={styles.hero}>
                <h1 className={styles.title}>Compass Ledger</h1>
                <p className={styles.tagline}>
                    Take control of your finances with simple, powerful budget tracking
                </p>
                <div className={styles.ctaButtons}>
                    <button
                        onClick={handleSignup}
                        className={styles.primaryButton}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Loading...' : 'Get Started'}
                    </button>
                    <button
                        onClick={handleLogin}
                        className={styles.secondaryButton}
                        disabled={isLoading}
                    >
                        Log In
                    </button>
                </div>
            </header>

            {/* Features Section */}
            <section className={styles.features}>
                <div className={styles.featureCard}>
                    <div className={styles.featureIcon}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                        </svg>
                    </div>
                    <h3 className={styles.featureTitle}>Track Expenses</h3>
                    <p className={styles.featureDescription}>
                        Log every transaction and see where your money goes
                    </p>
                </div>

                <div className={styles.featureCard}>
                    <div className={styles.featureIcon}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                    </div>
                    <h3 className={styles.featureTitle}>Monthly Budgets</h3>
                    <p className={styles.featureDescription}>
                        Set spending targets and stay on track month by month
                    </p>
                </div>

                <div className={styles.featureCard}>
                    <div className={styles.featureIcon}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                    </div>
                    <h3 className={styles.featureTitle}>Share with Family</h3>
                    <p className={styles.featureDescription}>
                        Collaborate on budgets with family members or partners
                    </p>
                </div>
            </section>

            {/* Footer */}
            <footer className={styles.footer}>
                <div className={styles.footerLinks}>
                    <button
                        onClick={onNavigateToPrivacy}
                        className={styles.footerLink}
                    >
                        Privacy Policy
                    </button>
                    <span className={styles.footerDivider}>|</span>
                    <button
                        onClick={onNavigateToTerms}
                        className={styles.footerLink}
                    >
                        Terms of Service
                    </button>
                </div>
                <p className={styles.copyright}>
                    Compass Ledger
                </p>
            </footer>
        </div>
    );
}

export default LandingPage;
